import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CreditCard,
  ExternalLink,
  CheckCircle,
  Circle,
  Plug,
  Landmark,
} from "lucide-react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { toast } from "../../../../utils/toast";
import useCobroStore from "../../../../store/cobro";
import { Button as UiButton } from "../../../../components/ui/Button";
import { Input as UiInput } from "../../../../components/ui/Input";
import { Select as UiSelect } from "../../../../components/ui/Select";
import { Switch } from "../../../../components/ui/Switch";
import { SearchableSelect } from "../../../../components/ui/SearchableSelect";
import { RUT_STATE, rutErrorKey, validateRut } from "../../../../utils/rut";
import {
  apiBodyMessage,
  apiBodyMessages,
  apiErrorData,
  apiErrorMessage,
  mapValidationErrors,
} from "../../../../utils/apiErrorMessage";
import { banksForCountry } from "./banks";

// ─────────────────────────────────────────────────────────────────────────────
// Linkify: el ID de cuenta NO aparece rotulado en su panel. El operador solo lo
// ve embebido dentro de su "Link para pagar"
// (app.linkify.cl/pay/<ID_CUENTA>/remote/<ID_PAGO>), y al lado tiene una columna
// "Email" — que es justo lo que un operador pegó acá en septiembre de 2026,
// dejando el link de pago apuntando a una cuenta inexistente. Aceptar el link
// completo convierte esa transcripción manual en un copy-paste.
// ─────────────────────────────────────────────────────────────────────────────

const LINKIFY_PAY_LINK_RE = /\/pay\/([^/?#]+)\/remote(?:[/?#]|$)/;
// Deliberadamente permisivo: la única muestra real es un opaco de 15
// alfanuméricos, y con n=1 fijar el largo exacto arriesga bloquear cuentas
// válidas. Lo que sí es seguro es que un ID opaco no lleva `@`, `/` ni espacios.
const LINKIFY_ACCOUNT_ID_RE = /^[A-Za-z0-9._-]{6,64}$/;

export function normalizeLinkifyAccountId(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const fromLink = LINKIFY_PAY_LINK_RE.exec(value);
  if (!fromLink) return value;
  try {
    return decodeURIComponent(fromLink[1]);
  } catch {
    return fromLink[1];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Proveedores de cobro disponibles por país. Se amplía a medida que se
// implementan más PSP (Mercado Pago, Wompi, Conekta, etc.).
//   - `country`:   ISO 3166-1 alpha-2 del país que atiende el proveedor.
//   - `fields`:    credenciales que pide cada proveedor. Cada campo admite
//                  `placeholder`, `hint`, `normalize(v)` y `validate(v)`.
//   - `logo`:      asset en /public (se sirve desde la raíz).
//   - `previewUrl`: si existe, muestra en vivo la URL que se generará con las
//                  credenciales tipeadas. Es la comprobación que faltaba: el
//                  operador ve el link roto ANTES de que lo vea un comercio.
// ─────────────────────────────────────────────────────────────────────────────

const PAYMENT_PROVIDERS = [
  {
    key: "flow",
    label: "Flow.cl",
    country: "CL",
    description:
      "Cobra a tus comercios en Chile con link de pago y QR (Webpay, transferencia, etc.).",
    docsUrl: "https://www.flow.cl/docs/api.html",
    logo: `${process.env.PUBLIC_URL}/logo-flow.svg`,
    fields: [
      { name: "apiKey", label: "API Key", type: "text" },
      { name: "secretKey", label: "Secret Key", type: "password" },
    ],
  },
  {
    key: "khipu",
    label: "Khipu",
    country: "CL",
    description:
      "Transferencia bancaria instantánea (A2A) — la opción más económica. Link de cobro y QR.",
    docsUrl: "https://docs.khipu.com/",
    logo: `${process.env.PUBLIC_URL}/logo-khipu.png`,
    fields: [{ name: "apiKey", label: "API Key", type: "password" }],
  },
  {
    key: "mercadopago",
    label: "Mercado Pago",
    country: "CL",
    description:
      "Link de pago y QR con la billetera Mercado Pago, tarjetas y transferencia.",
    docsUrl: "https://www.mercadopago.cl/developers",
    logo: `${process.env.PUBLIC_URL}/logo-mercadopago.svg`,
    fields: [{ name: "accessToken", label: "Access Token", type: "password" }],
  },
  {
    key: "transbank",
    label: "Transbank Webpay",
    country: "CL",
    description:
      "El rail de tarjetas dominante en Chile (crédito y débito) vía Webpay Plus.",
    docsUrl: "https://www.transbankdevelopers.cl/producto/webpay",
    logo: `${process.env.PUBLIC_URL}/logo-webpay.svg`,
    fields: [
      { name: "commerceCode", label: "Código de Comercio", type: "text" },
      { name: "apiKey", label: "API Key Secret", type: "password" },
    ],
  },
  {
    key: "linkify",
    label: "Linkify",
    country: "CL",
    description:
      "Transferencia bancaria con validación automática: tú defines el cobro y Linkify concilia la transferencia recibida.",
    docsUrl: "https://www.linkify.cl/documentos/integracion-sistema-externo.pdf",
    logo: `${process.env.PUBLIC_URL}/linkify-logo.jpg`,
    // En Linkify: Mi Linkify > Cuentas > Acciones > Ver datos de integración.
    fields: [
      {
        name: "accountId",
        label: "ID de cuenta",
        type: "text",
        placeholder: "Pega aquí el código o el link para pagar completo",
        hint: "Es el código de tu “Link para pagar”: app.linkify.cl/pay/ESTE-CÓDIGO/remote/… No es tu correo ni el nombre de la cuenta. También puedes pegar el link completo.",
        normalize: normalizeLinkifyAccountId,
        validate: (v) =>
          LINKIFY_ACCOUNT_ID_RE.test(v)
            ? null
            : "Ese no es el ID de cuenta de Linkify. Copia el código que aparece en tu “Link para pagar”, o pega el link completo.",
      },
      { name: "secretKey", label: "Llave secreta", type: "password" },
    ],
    previewUrl: (creds) =>
      creds.accountId
        ? `https://app.linkify.cl/pay/${creds.accountId}/remote/…`
        : "",
  },
];

// Mapea nombres de país comunes (operationsAddresses) a ISO alpha-2.
const COUNTRY_NAME_TO_ISO = {
  chile: "CL",
  argentina: "AR",
  peru: "PE",
  "perú": "PE",
  colombia: "CO",
  venezuela: "VE",
  uruguay: "UY",
  mexico: "MX",
  "méxico": "MX",
};

// Resuelve el país del operador: campo canónico `country` o, si falta, la
// primera operationsAddress. Devuelve ISO alpha-2 o null si no se puede resolver.
function resolveOperatorCountry(company) {
  const raw =
    company?.country ||
    company?.operationsCountry ||
    company?.operationsAddresses?.[0]?.country;
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  return COUNTRY_NAME_TO_ISO[trimmed.toLowerCase()] ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal de configuración de credenciales
// ─────────────────────────────────────────────────────────────────────────────

function ConfigModal({ provider, config, isOpen, toggle, onSaved }) {
  const { t } = useTranslation();
  const { saving, savePaymentConfig } = useCobroStore((s) => ({
    saving: s.saving,
    savePaymentConfig: s.savePaymentConfig,
  }));
  const [environment, setEnvironment] = useState("sandbox");
  const [creds, setCreds] = useState({});
  const [savedUrl, setSavedUrl] = useState("");
  // Secretos que el operador decidió reemplazar: solo esos viajan llenos. Los
  // demás se omiten del payload (el backend mergea contra lo guardado), pero la
  // omisión SOLO es válida si al menos otra credencial viaja — ver needsReentry.
  const [reemplazar, setReemplazar] = useState({});
  // El DTO del backend exige `credentials` no vacío (`@IsNotEmptyObject`), así que
  // un guardado en el que TODAS las credenciales se omitieron se rechaza con un 400
  // antes del merge que habría conservado las guardadas. Cuando eso aplica, se le
  // pide al operador reingresar la llave en vez de mandar un payload condenado.
  const [needsReentry, setNeedsReentry] = useState(false);

  // Campos no secretos que el backend devuelve en claro, para prellenar el formulario.
  const guardadas = config?.credentials ?? {};
  // Por campo: si hay algo guardado, aunque no se pueda mostrar.
  const cargadas = config?.credentialsSet ?? {};

  useEffect(() => {
    if (isOpen) {
      // Hidratar desde lo guardado: forzar "sandbox" al reconfigurar degradaba en
      // silencio a los proveedores que sí cambian de host según el ambiente
      // (Flow y Transbank) con solo abrir el modal y volver a guardar.
      setEnvironment(config?.environment ?? "sandbox");
      setCreds({ ...(config?.credentials ?? {}) });
      setSavedUrl("");
      setReemplazar({});
      setNeedsReentry(false);
    }
    // `config?.credentials` es un objeto nuevo en cada render del padre; se depende de
    // su contenido serializado para no re-hidratar (y borrar lo tipeado) sin motivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, provider, config?.environment, JSON.stringify(config?.credentials)]);

  if (!provider) return null;

  // Linkify es pull: el operador debe pegar SU URL de integración en Linkify.
  const isLinkify = provider.key === "linkify";
  const integrationUrl = savedUrl || config?.integrationUrl || "";

  // Normaliza lo tipeado (trim + normalizador propio del campo). Se usa tanto para
  // el preview en vivo como para lo que efectivamente se guarda, así que lo que el
  // operador ve es exactamente lo que se persiste.
  const normalizeCreds = (raw) =>
    provider.fields.reduce((acc, f) => {
      const value = raw[f.name] ?? "";
      acc[f.name] = f.normalize ? f.normalize(value) : String(value).trim();
      return acc;
    }, {});

  const normalized = normalizeCreds(creds);
  const previewUrl = provider.previewUrl?.(normalized) ?? "";

  // Un campo está BLOQUEADO solo si está guardado y el backend NO devolvió su valor:
  // eso es exactamente la definición de secreto acá (el provider declara qué campos
  // son públicos; el resto nunca sale del microservicio). Un campo público guardado
  // llega con su valor y tiene que quedar editable y prellenado — bloquearlo también
  // dejaría el formulario tan opaco como antes.
  const esSecretoGuardado = (f) =>
    !!cargadas[f.name] && guardadas[f.name] === undefined;
  // Un secreto guardado bloqueado se omite del payload y el backend conserva el que
  // tiene — siempre que viaje al menos OTRA credencial que dispare el merge. Si no
  // viaja ninguna, el guardado no puede completarse sin reingresar una llave.
  const bloqueado = (f) => esSecretoGuardado(f) && !reemplazar[f.name];

  const handleSave = async () => {
    const missing = provider.fields.find(
      (f) => !normalized[f.name] && !bloqueado(f),
    );
    if (missing) {
      toast.error(t("Ingresa {{label}}", { label: missing.label }));
      return;
    }
    // Validación de forma ANTES de guardar. Sin esto, un valor con la forma
    // equivocada se cifra tal cual y solo se descubre roto cuando un comercio
    // aterriza en la URL de pago del proveedor.
    const invalid = provider.fields.find(
      (f) => f.validate && f.validate(normalized[f.name]),
    );
    if (invalid) {
      toast.error(t(invalid.validate(normalized[invalid.name])));
      return;
    }
    // Se omiten los vacíos a propósito: el backend mergea contra lo guardado, así
    // que omitir un secreto significa "no lo toqué". Mandarlo vacío lo borraría. El
    // merge, eso sí, solo corre si viaja al menos una credencial: el DTO rechaza
    // `{}` con un 400 antes de llegar a él.
    const credentialsPayload = Object.fromEntries(
      Object.entries(normalized).filter(([, v]) => v !== ""),
    );
    // Caso "solo cambié el ambiente": nada viaja y todos los campos vacíos están
    // bloqueados. El backend contestaría 400 sin tocar lo guardado, así que se
    // explica acá lo único que el operador puede hacer para guardar.
    if (
      Object.keys(credentialsPayload).length === 0 &&
      provider.fields.some((f) => bloqueado(f))
    ) {
      setNeedsReentry(true);
      toast.error(t("Para guardar cambios vuelve a ingresar la llave del proveedor."));
      return;
    }
    try {
      const saved = await savePaymentConfig({
        provider: provider.key,
        country: provider.country,
        environment,
        credentials: credentialsPayload,
      });
      setNeedsReentry(false);
      toast.success(t("Proveedor de pago configurado"));
      onSaved?.();
      if (isLinkify) {
        // No cerramos: mostramos la URL de integración a pegar en Linkify.
        setSavedUrl(saved?.integrationUrl ?? config?.integrationUrl ?? "");
      } else {
        toggle();
      }
    } catch (err) {
      // apiErrorMessage une los arrays del ValidationPipe con " · ": el array crudo
      // en inglés no es texto mostrable.
      toast.error(apiErrorMessage(err, t("No se pudo guardar la configuración")));
    }
  };

  const copyUrl = () => {
    if (!integrationUrl) return;
    navigator.clipboard?.writeText(integrationUrl);
    toast.success(t("URL copiada"));
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {t("Configurar {{label}}", { label: provider.label })}
      </ModalHeader>
      <ModalBody>
        {isLinkify && (
          <div className="bill-hint" style={{ marginBottom: "0.75rem" }}>
            {t("En Linkify: ")}
            <strong>{t("Mi Linkify → Cuentas → Acciones → Ver datos de integración")}</strong>
            {t(" para copiar tu ID de cuenta y tu llave secreta.")}
          </div>
        )}
        <FormGroup>
          <Label className="bill-label">{t("Ambiente")}</Label>
          <Input
            type="select"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
          >
            <option value="sandbox">{t("Sandbox (pruebas)")}</option>
            <option value="production">{t("Producción")}</option>
          </Input>
        </FormGroup>
        {provider.fields.map((f) => {
          // Un secreto guardado no se puede mostrar (el backend nunca lo devuelve),
          // pero tampoco hay que obligar a reingresarlo para corregir otro campo: se
          // muestra bloqueado y con un botón explícito para reemplazarlo. La excepción
          // es cuando no viaja ninguna credencial: ahí el guardado sí exige reingresar
          // una llave (needsReentry), y se avisa bajo cada campo bloqueado.
          const estaBloqueado = bloqueado(f);
          const mostrarAvisoReentry = estaBloqueado && needsReentry;
          const describedBy = [
            !estaBloqueado && f.hint ? `payment-cred-${f.name}-hint` : null,
            mostrarAvisoReentry ? `payment-cred-${f.name}-reentry` : null,
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <FormGroup key={f.name}>
              <Label className="bill-label" for={`payment-cred-${f.name}`}>
                {t(f.label)}
              </Label>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <Input
                  id={`payment-cred-${f.name}`}
                  type={f.type}
                  autoComplete="off"
                  disabled={estaBloqueado}
                  placeholder={
                    estaBloqueado
                      ? t("Guardada · no se puede mostrar")
                      : (f.placeholder ?? "")
                  }
                  aria-describedby={describedBy || undefined}
                  value={estaBloqueado ? "" : (creds[f.name] ?? "")}
                  onChange={(e) =>
                    setCreds((c) => ({ ...c, [f.name]: e.target.value }))
                  }
                />
                {estaBloqueado && (
                  <Button
                    color="secondary"
                    outline
                    onClick={() => {
                      setReemplazar((r) => ({ ...r, [f.name]: true }));
                      setCreds((c) => ({ ...c, [f.name]: "" }));
                      setNeedsReentry(false);
                    }}
                  >
                    {t("Cambiar")}
                  </Button>
                )}
              </div>
              {f.hint && !estaBloqueado && (
                <small
                  id={`payment-cred-${f.name}-hint`}
                  className="text-muted"
                  style={{ display: "block", marginTop: "0.25rem" }}
                >
                  {t(f.hint)}
                </small>
              )}
              {mostrarAvisoReentry && (
                <p
                  id={`payment-cred-${f.name}-reentry`}
                  className="text-xs text-status-error mt-1"
                  role="alert"
                >
                  {t("Para guardar cambios vuelve a ingresar la llave del proveedor.")}
                </p>
              )}
            </FormGroup>
          );
        })}

        {/* Preview en vivo del link que se generará. Es lo único que habría hecho
            evidente el error de configuración: la "URL de integración" que ya se
            mostraba se construye solo con el _id de la config y NO depende de las
            credenciales, así que se veía perfecta con el dato malo cargado. */}
        {previewUrl && (
          <div className="bill-hint" style={{ marginBottom: "0.75rem" }}>
            {t("Tus comercios pagarán en:")}{" "}
            <code style={{ wordBreak: "break-all" }}>{previewUrl}</code>
          </div>
        )}
        <small className="text-muted">
          {t("Las credenciales se almacenan cifradas (AES-256-GCM); las secretas no se vuelven a mostrar.")}{" "}
          {t("Los fondos liquidan directo a tu cuenta del proveedor.")}
        </small>

        {/* Antes del primer guardado no hay URL que mostrar: se construye con el _id
            de la configuración, que todavía no existe. Se dice explícitamente en vez
            de no mostrar nada, que se lee como que falta algo. */}
        {isLinkify && !integrationUrl && (
          <div className="bill-hint" style={{ marginTop: "1rem" }}>
            {t("Tu URL de integración se genera al guardar. Aparecerá acá para que la pegues en Linkify.")}
          </div>
        )}

        {isLinkify && integrationUrl && (
          <div
            style={{
              marginTop: "1rem",
              paddingTop: "0.75rem",
              borderTop: "1px solid var(--border-color, #e5e7eb)",
            }}
          >
            <Label className="bill-label">{t("Tu URL de integración")}</Label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <Input readOnly value={integrationUrl} onFocus={(e) => e.target.select()} />
              <Button color="secondary" outline onClick={copyUrl}>
                {t("Copiar")}
              </Button>
            </div>
            <small className="text-muted" style={{ display: "block", marginTop: "0.5rem" }}>
              {t("Pégala en Linkify (")}
              <strong>{t("Mi Linkify → Cuentas → Acciones → Editar")}</strong>
              {t("),")}{" "}
              <strong>{t("habilita los cobros remotos")}</strong>
              {t(" y guarda. Con eso Linkify podrá consultar el monto y notificarnos los pagos.")}
            </small>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>
          {isLinkify && integrationUrl ? t("Cerrar") : t("Cancelar")}
        </Button>
        <Button color="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <Spinner size="sm" />
          ) : isLinkify && integrationUrl ? (
            t("Actualizar")
          ) : (
            t("Guardar")
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tarjeta de proveedor
// ─────────────────────────────────────────────────────────────────────────────

function StatusBadge({ connected, environment }) {
  const { t } = useTranslation();
  if (connected) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-status-success-soft px-2 py-0.5 text-xs font-medium text-status-success">
        <CheckCircle size={11} aria-hidden />
        {environment
          ? t("Conectado · {{environment}}", { environment })
          : t("Conectado")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-bg-subtle px-2 py-0.5 text-xs font-medium text-fg-muted">
      <Circle size={11} aria-hidden />
      {t("Sin configurar")}
    </span>
  );
}

function ProviderCard({ provider, config, onConfigure }) {
  const { t } = useTranslation();
  const connected = !!config?.isActive;
  return (
    <article
      className={`flex flex-col rounded-xl border bg-bg shadow-card transition-shadow hover:shadow-elevated ${
        connected ? "border-status-success/40" : "border-border"
      }`}
    >
      <div className="flex items-start gap-3 p-4 sm:p-5">
        {/* Logo en tile contenido. El fondo blanco se pone inline (no con la
            clase `bg-white`, que un override de dark-mode fuerza a superficie
            oscura); `dark-safe-img` evita que el filtro global invierta el logo. */}
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border ${
            provider.logo
              ? "border-border p-2"
              : "border-transparent bg-brand-50 text-brand-600"
          }`}
          style={provider.logo ? { backgroundColor: "#fff" } : undefined}
          aria-hidden="true"
        >
          {provider.logo ? (
            <img
              src={provider.logo}
              alt=""
              className="dark-safe-img h-full w-full object-contain"
            />
          ) : (
            <CreditCard size={22} />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="m-0 text-[15px] font-semibold text-fg">
              {provider.label}
            </h3>
            <StatusBadge connected={connected} environment={config?.environment} />
          </div>
          <p className="mb-0 mt-1.5 text-sm leading-relaxed text-fg-muted">
            {t(provider.description)}
          </p>
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-border px-4 py-3 sm:px-5">
        {provider.docsUrl && (
          <a
            href={provider.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-fg-muted no-underline transition-colors hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            aria-label={t("Documentación de {{label}}", { label: provider.label })}
          >
            <ExternalLink size={15} aria-hidden />
            Docs
          </a>
        )}
        <UiButton
          variant={connected ? "outline" : "primary"}
          size="md"
          iconLeft={<Plug size={15} />}
          onClick={() => onConfigure(provider)}
        >
          {connected ? t("Reconfigurar") : t("Conectar")}
        </UiButton>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Datos bancarios para "Transferencia manual"
// ─────────────────────────────────────────────────────────────────────────────

const ACCOUNT_TYPES = [
  { value: "corriente", label: "Cuenta Corriente" },
  { value: "vista", label: "Cuenta Vista" },
  { value: "ahorro", label: "Cuenta de Ahorro" },
  { value: "rut", label: "Cuenta RUT" },
];

const EMPTY_BANK = {
  bankName: "",
  accountType: "",
  accountNumber: "",
  holderName: "",
  holderRut: "",
  notifyEmail: "",
  acceptManualTransfer: false,
  acceptCash: false,
};

// Dominio con etiquetas alfanuméricas (guiones interiores) y TLD de 2+ letras: es
// lo más cerca de @IsEmail que se puede estar sin rechazar correos válidos. La
// versión anterior aceptaba "x@dominio.c", que el backend rechaza.
// El rango no ASCII replica el que acepta isFQDN (validator.js, detrás de @IsEmail):
// cubre dominios internacionalizados como "ñandú.cl" o "café.fr". El TLD admite
// además punycode ("empresa.xn--p1ai"), que isFQDN valida con su propia alternativa
// — sin ella se rechazarían en el front correos que el backend sí guarda.
const IDN_LETTER = "\\u00a1-\\uffff";
const EMAIL_RE = new RegExp(
  `^[^\\s@]+@(?:[A-Za-z0-9${IDN_LETTER}](?:[A-Za-z0-9${IDN_LETTER}-]*[A-Za-z0-9${IDN_LETTER}])?\\.)+(?:[A-Za-z${IDN_LETTER}]{2,}|xn--[A-Za-z0-9-]{2,})$`,
);
const emailOk = (v) => !v || EMAIL_RE.test(v);

// Etiquetas VISIBLES del formulario de datos bancarios, para que el error del
// backend se lea como el campo que el operador tiene delante.
const BANK_FIELD_LABELS = {
  bankName: "Banco",
  accountType: "Tipo de cuenta",
  accountNumber: "N° de cuenta",
  holderName: "Titular",
  holderRut: "RUT del titular",
  notifyEmail: "Correo de aviso",
};

// class-validator responde en inglés y con el nombre técnico del campo; se traduce
// solo el motivo. Cualquier mensaje no reconocido cae a "valor no válido" en vez de
// mostrarle al operador la cadena en inglés.
function bankFieldReason(message, t) {
  const maxLength = /must be shorter than or equal to (\d+) characters/.exec(message);
  if (maxLength) return t("supera el máximo de {{max}} caracteres", { max: maxLength[1] });
  if (message.includes("must be an email")) return t("formato de correo no válido");
  return t("valor no válido");
}

/**
 * Texto mostrable de un error al guardar los datos bancarios. Los 400 del
 * ValidationPipe traen un array de mensajes en inglés por campo; se renderizan
 * como "<Campo visible>: <motivo>" y el resto del body (403 en español, mensajes
 * de negocio) se agrega tal cual. Sin nada usable cae al mensaje genérico.
 * @param {unknown} err
 * @param {(key: string, opts?: object) => string} t
 * @returns {string|null}
 */
export function bankSaveErrorMessage(err, t) {
  const { errors, unmatched } = mapValidationErrors(apiBodyMessages(apiErrorData(err)), {
    bankName: "bankName",
    accountType: "accountType",
    accountNumber: "accountNumber",
    holderName: "holderName",
    holderRut: "holderRut",
    notifyEmail: "notifyEmail",
  });
  const parts = Object.keys(errors).map(
    (field) => `${t(BANK_FIELD_LABELS[field])}: ${bankFieldReason(errors[field], t)}`,
  );
  // apiBodyMessage descarta además el ruido de transporte ('Network Error', HTML de
  // un proxy): sin eso, un fallo de red se mostraría como "Network Error".
  const unmatchedText = apiBodyMessage(unmatched);
  if (unmatchedText) parts.push(unmatchedText);
  return parts.length
    ? parts.join(" · ")
    : apiErrorMessage(err, t("No se pudieron guardar los datos"));
}

// Campo de formulario: label asociado (click enfoca) + control.
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-fg-muted mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

const OTHER_BANK = "__other__";

// Campo "Banco": selector con búsqueda según el país de operación del operador,
// con opción "Otro banco" (texto libre) para no quedar atados si la nómina cambia
// o falta un banco. Si el país no tiene nómina, cae a un input de texto.
function BancoField({ country, value, onChange }) {
  const { t } = useTranslation();
  const list = banksForCountry(country);
  const [other, setOther] = useState(false);

  // Si el valor guardado no está en la nómina (banco custom o nómina vieja),
  // entra en modo "Otro" para mostrar el texto y no perderlo.
  useEffect(() => {
    if (value && list && !list.includes(value)) setOther(true);
  }, [value, list]);

  if (!list) {
    return (
      <UiInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("Nombre del banco")}
        maxLength={120}
      />
    );
  }

  const options = [
    ...list.map((b) => ({ value: b, label: b })),
    { value: OTHER_BANK, label: t("Otro banco (especificar)") },
  ];
  const selectValue = other ? OTHER_BANK : list.includes(value) ? value : "";

  return (
    <div className="flex flex-col gap-2">
      <SearchableSelect
        options={options}
        value={selectValue}
        onChange={(v) => {
          if (v === OTHER_BANK) {
            setOther(true);
          } else {
            setOther(false);
            onChange(v);
          }
        }}
        placeholder={t("Selecciona tu banco")}
        isClearable
        noOptionsText={t("Sin coincidencias")}
      />
      {other && (
        <UiInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("Nombre del banco")}
          maxLength={120}
          autoFocus
        />
      )}
    </div>
  );
}

function BankSettingsCard({ country }) {
  const { t } = useTranslation();
  const { bankSettings, fetchBankSettings, saveBankSettings, savingBank } =
    useCobroStore((s) => ({
      bankSettings: s.bankSettings,
      fetchBankSettings: s.fetchBankSettings,
      saveBankSettings: s.saveBankSettings,
      savingBank: s.savingBank,
    }));
  const [form, setForm] = useState(EMPTY_BANK);

  useEffect(() => {
    fetchBankSettings();
  }, [fetchBankSettings]);

  // Precarga: a diferencia de las credenciales PSP (write-only), los datos
  // bancarios sí se hidratan para poder editarlos.
  useEffect(() => {
    if (!bankSettings) return;
    setForm({
      bankName: bankSettings.bankName ?? "",
      accountType: bankSettings.accountType ?? "",
      accountNumber: bankSettings.accountNumber ?? "",
      holderName: bankSettings.holderName ?? "",
      holderRut: bankSettings.holderRut ?? "",
      notifyEmail: bankSettings.notifyEmail ?? "",
      acceptManualTransfer: !!bankSettings.acceptManualTransfer,
      acceptCash: !!bankSettings.acceptCash,
    });
  }, [bankSettings]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // El RUT del titular es la llave con la que el comercio transfiere a la cuenta
  // correcta; un DV errado manda el dinero a una cuenta inexistente. Se valida con
  // módulo 11 y se frena el guardado mientras no pase.
  // El módulo 11 es chileno: con un operador de otro país este campo lleva su documento
  // local (CUIT, NIT), así que la validación se limita a CL.
  const esChile = country === "CL";
  const rutCheck = validateRut(form.holderRut);
  const [rutTouched, setRutTouched] = useState(false);
  // El DV equivocado se avisa al tiro (es definitivo); "incompleto" solo al salir del
  // campo, para no marcar en rojo a quien todavía está escribiendo.
  const rutError =
    esChile &&
    (rutCheck.state === RUT_STATE.BAD_DV ||
      (rutTouched && rutCheck.state === RUT_STATE.INCOMPLETE))
      ? rutErrorKey(rutCheck)
      : null;
  // Solo bloquea con el campo a la vista: si la transferencia manual está apagada el
  // formulario no muestra el RUT, y un valor histórico inválido (o a medio corregir)
  // no debe impedir guardar el resto de la configuración.
  const rutBlocks =
    esChile &&
    form.acceptManualTransfer &&
    rutCheck.state !== RUT_STATE.EMPTY &&
    rutCheck.state !== RUT_STATE.OK;
  const RUT_ERROR_ID = "bank-holder-rut-error";

  const handleSave = async () => {
    if (rutBlocks) return;

    if (form.acceptManualTransfer) {
      const required = [
        "bankName",
        "accountType",
        "accountNumber",
        "holderName",
        "holderRut",
      ];
      const missing = required.find((k) => !String(form[k] ?? "").trim());
      if (missing) {
        toast.error(
          t("Completa los datos bancarios para aceptar transferencias."),
        );
        return;
      }
    }
    // Se valida el valor ya trimmeado: es el que efectivamente se envía.
    if (!emailOk(form.notifyEmail?.trim())) {
      toast.error(t("El correo de aviso no tiene un formato válido."));
      return;
    }
    try {
      await saveBankSettings({
        ...form,
        notifyEmail: form.notifyEmail?.trim() || undefined,
      });
      toast.success(t("Datos de transferencia guardados"));
    } catch (err) {
      toast.error(bankSaveErrorMessage(err, t));
    }
  };

  return (
    <section
      className="rounded-xl border border-border bg-bg shadow-card"
      style={{ padding: "1.5rem", marginTop: "1.5rem" }}
    >
      {/* Encabezado */}
      <div className="flex items-start gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-brand-50 text-brand-600 shrink-0">
          <Landmark className="w-5 h-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-fg" style={{ margin: 0 }}>
            {t("Transferencia y efectivo")}
          </h3>
          <p className="text-sm text-fg-muted" style={{ margin: "0.25rem 0 0" }}>
            {t("Ofrece a tus comercios pagar por transferencia o efectivo. Cada pago queda en revisión hasta que tú lo confirmes.")}
          </p>
        </div>
      </div>

      {/* Transferencia manual: toggle + datos bancarios (revelado al activar) */}
      <div
        className="rounded-lg border border-border overflow-hidden"
        style={{ marginTop: "1.25rem" }}
      >
        <div className="flex items-center justify-between gap-4 p-4">
          <div className="min-w-0">
            <div className="font-medium text-fg">{t("Transferencia manual")}</div>
            <p
              className="text-sm text-fg-muted"
              style={{ margin: "0.125rem 0 0" }}
            >
              {t("Muestra tus datos bancarios al comercio. Confirmas cuando recibas el comprobante.")}
            </p>
          </div>
          <Switch
            checked={form.acceptManualTransfer}
            onChange={(v) => setField("acceptManualTransfer", v)}
            ariaLabel={t("Aceptar transferencia manual")}
          />
        </div>

        {form.acceptManualTransfer && (
          <div className="border-t border-border bg-bg-subtle p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="block text-xs font-medium text-fg-muted mb-1.5">
                  {t("Banco")}
                </span>
                <BancoField
                  country={country}
                  value={form.bankName}
                  onChange={(v) => setField("bankName", v)}
                />
              </div>
              <Field label={t("Tipo de cuenta")}>
                <UiSelect
                  value={form.accountType}
                  onChange={(e) => setField("accountType", e.target.value)}
                >
                  <option value="">{t("Selecciona…")}</option>
                  {ACCOUNT_TYPES.map((at) => (
                    <option key={at.value} value={at.value}>
                      {t(at.label)}
                    </option>
                  ))}
                </UiSelect>
              </Field>
              <Field label={t("N° de cuenta")}>
                <UiInput
                  value={form.accountNumber}
                  onChange={(e) => setField("accountNumber", e.target.value)}
                  placeholder={t("Ej: 12345678")}
                  maxLength={60}
                />
              </Field>
              <Field label={t("Titular")}>
                <UiInput
                  value={form.holderName}
                  onChange={(e) => setField("holderName", e.target.value)}
                  placeholder={t("Nombre del titular")}
                  maxLength={160}
                />
              </Field>
              <Field label={t("RUT del titular")}>
                <UiInput
                  id="bank-holder-rut"
                  value={form.holderRut}
                  onChange={(e) => setField("holderRut", e.target.value)}
                  onBlur={() => setRutTouched(true)}
                  placeholder="12.345.678-9"
                  maxLength={20}
                  invalid={!!rutError}
                  aria-invalid={!!rutError}
                  aria-describedby={rutError ? RUT_ERROR_ID : undefined}
                />
                {rutError && (
                  <p
                    id={RUT_ERROR_ID}
                    className="text-xs text-status-error mt-1"
                    role="alert"
                  >
                    {t(rutError, { dv: rutCheck.expectedDv })}
                  </p>
                )}
              </Field>
              <Field label={t("Correo de aviso (opcional)")}>
                <UiInput
                  type="email"
                  value={form.notifyEmail}
                  onChange={(e) => setField("notifyEmail", e.target.value)}
                  placeholder="pagos@tuempresa.cl"
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      {/* Efectivo: solo un toggle (sin datos bancarios) */}
      <div
        className="flex items-center justify-between gap-4 rounded-lg border border-border p-4"
        style={{ marginTop: "0.75rem" }}
      >
        <div className="min-w-0">
          <div className="font-medium text-fg">{t("Efectivo")}</div>
          <p
            className="text-sm text-fg-muted"
            style={{ margin: "0.125rem 0 0" }}
          >
            {t("El comercio informa que pagará en efectivo. No requiere datos bancarios.")}
          </p>
        </div>
        <Switch
          checked={form.acceptCash}
          onChange={(v) => setField("acceptCash", v)}
          ariaLabel={t("Aceptar efectivo")}
        />
      </div>

      <div className="flex justify-end" style={{ marginTop: "1.25rem" }}>
        <UiButton
          variant="primary"
          onClick={handleSave}
          loading={savingBank}
          disabled={rutBlocks}
        >
          {t("Guardar")}
        </UiButton>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sección principal
// ─────────────────────────────────────────────────────────────────────────────

export default function PaymentIntegrationsSection({ company }) {
  const { t } = useTranslation();
  const { paymentConfigs, fetchPaymentConfig } = useCobroStore((s) => ({
    paymentConfigs: s.paymentConfigs,
    fetchPaymentConfig: s.fetchPaymentConfig,
  }));
  const [modalProvider, setModalProvider] = useState(null);

  useEffect(() => {
    fetchPaymentConfig();
  }, [fetchPaymentConfig]);

  const configFor = (key) => paymentConfigs.find((c) => c.provider === key);

  // Filtra por país del operador. Si no se puede resolver, muestra todos los
  // proveedores (no bloquea la configuración).
  const country = resolveOperatorCountry(company);
  const visible = country
    ? PAYMENT_PROVIDERS.filter((p) => p.country === country)
    : PAYMENT_PROVIDERS;

  if (country && visible.length === 0) {
    return (
      <div className="text-sm text-fg-muted" style={{ padding: "1rem 0" }}>
        {t(
          "Aún no hay proveedores de pago disponibles para tu país ({{country}}). Estamos sumando más operadores.",
          { country },
        )}
      </div>
    );
  }

  return (
    <>
      {!country && (
        <p className="text-sm text-fg-muted mb-3">
          {t("Define el país de tu empresa en")}{" "}
          <strong>{t("General → Organización")}</strong>
          {t(" para ver los proveedores de tu mercado.")}
        </p>
      )}

      <section>
        <div className="flex items-start gap-3">
          <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-brand-50 text-brand-600 shrink-0">
            <CreditCard className="w-5 h-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-fg m-0">
              {t("Pasarelas de pago")}
            </h3>
            <p className="text-sm text-fg-muted mt-1 mb-0">
              {t("Cobra a tus comercios con link de pago o QR. Los fondos liquidan directo a tu cuenta del proveedor.")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          {visible.map((p) => (
            <ProviderCard
              key={p.key}
              provider={p}
              config={configFor(p.key)}
              onConfigure={setModalProvider}
            />
          ))}
        </div>
      </section>

      <ConfigModal
        provider={modalProvider}
        config={configFor(modalProvider?.key)}
        isOpen={!!modalProvider}
        toggle={() => setModalProvider(null)}
        onSaved={fetchPaymentConfig}
      />

      <BankSettingsCard country={country} />
    </>
  );
}
